import React, { useState, useEffect, useMemo } from 'react';
import { useContractDetail } from '../../../../contextsProperty/ContextContractDetail';
import useTranslate from '../../../../../customHooks/useTranslate';
import { Alert } from '@mui/material';
import {
    doubleMD_ColumnGrid,
    gridSpacing20,
    gridSpacing30,
    singleLG_DoubleSM_ColumnGrid,
} from '../../../../../utils/commonVariables/grid';
import Grid from '../../../../../muiComponents/Grid';
import GridChild from '../../../../../muiComponents/GridChild';
import { RATE_SCOPE_MODELS } from '../../../../../utils/contractUtils/contractUtils';
import LateralPanel from '../../../../../muiComponents/LateralPanel';
import Title from '../../../../components/Title';
import TextInput from '../../../../../muiComponents/TextInput/TextInput';
import RatesByRoom from './ratesByScopeModel/RatesByRoom';
import RatesByOccupancy from './ratesByScopeModel/RatesByOccupancy';
import RatesByPerson from './ratesByScopeModel/RatesByPerson';
import Button from '../../../../../muiComponents/Button';
import {
    IOccupancyRateOccupancy,
    IOccupancyRatePersonRate,
} from '../../../../../types/entities/IStaticRateDateOccupancyRateParsed';
import IconButton from '../../../../../muiComponents/IconButton';
import DeletePopUp from '../DeletePopUp';
import { FeedbackMessages } from '../../../../../utils/commonVariables/messages';
import { useNotification } from '../../../../../contexts/NotificationsContext';
import LoadingPanel from './LoadingPanel';
import PanelTitle from '../../../yourProperties/Title';
import useUserProfile from '../../../../../customHooks/useUserProfile';
import ReadOnlyUserAlert from '../../../ReadOnlyUserAlert';

interface RatesPanelProps {
    open: boolean;
    onClose: () => void;
}

type FieldToValidateValue =
    | number
    | IOccupancyRateOccupancy[]
    | IOccupancyRatePersonRate[]
    | undefined;

interface IFieldToValidate {
    value: FieldToValidateValue;
    validator: (value: FieldToValidateValue) => boolean;
    errorMessage: string;
}

const RatesPanel = (props: RatesPanelProps) => {
    const { open, onClose } = props;
    const translate = useTranslate();
    const { setErrorMessage, setSuccessMessage } = useNotification();
    const { readOnlyUser } = useUserProfile();
    const [deletePopUpVisible, setDeletePopUpVisible] = useState(false);
    const {
        isLoadingPanel,
        handleLoadingPanel,
        handleChangeRateDateEdition,
        rateDateEdition,
        isBlockedContract,
        handleSaveRateDate,
    } = useContractDetail();

    const isNewRate =
        parseInt(rateDateEdition.id) < 0 && rateDateEdition.__isAdded;

    const handleInputNumber = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        handleChangeRateDateEdition({
            [name]: parseInt(value),
            __isChanged: true,
        });
    };

    const [isFullscreen, setIsFullscreen] = useState(false);

    const [validatedForm, setValidatedForm] = useState(false);
    const [errors, setErrors] = useState<Record<string, string>>({});

    const getFieldsToValidate = () => {
        const fieldsToValidateBase: Record<string, IFieldToValidate> = {
            initialInventory: {
                value: rateDateEdition.initialInventory,
                validator: value => !!value,
                errorMessage: 'required',
            },
            currentInventory: {
                value: rateDateEdition.currentInventory,
                validator: value =>
                    !!value &&
                    Number(value) <= rateDateEdition.initialInventory,
                errorMessage: 'smaller_equal_initial_inventory',
            },
        };

        if (rateScopeId === RATE_SCOPE_MODELS.ROOM) {
            fieldsToValidateBase.baseRateRoom = {
                value: rateDateEdition?.occupancyRates?.room?.abt,
                validator: value => !!value,
                errorMessage: 'required',
            };
        }

        if (rateScopeId === RATE_SCOPE_MODELS.OCCUPANCY) {
            fieldsToValidateBase.baseRateOccupancy = {
                value: rateDateEdition?.occupancyRates?.occupancies,
                validator: value =>
                    Array.isArray(value) && value.some(occ => occ.abt),
                errorMessage: 'required_rate',
            };
        }

        if (rateScopeId === RATE_SCOPE_MODELS.PERSON) {
            fieldsToValidateBase.baseRatePerson = {
                value: rateDateEdition?.occupancyRates?.persons?.rates,
                validator: value =>
                    Array.isArray(value) && value.some(occ => occ.abt),
                errorMessage: 'required_rate',
            };
        }
        return fieldsToValidateBase;
    };

    const validateForm = () => {
        const fields = getFieldsToValidate();
        const newErrors: Record<string, string> = {};

        Object.entries(fields).forEach(([key, field]) => {
            if (!field.validator(field.value)) {
                newErrors[key] = field.errorMessage;
            }
        });

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    useEffect(() => {
        if (validatedForm) {
            validateForm();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rateDateEdition, validatedForm]);

    const rateScopeId =
        rateDateEdition.idRoomScopeModel || rateDateEdition.idRateScopeModel;

    const panelClassname = useMemo(() => {
        if (isFullscreen) return '--full-width';
        if (rateScopeId === RATE_SCOPE_MODELS.OCCUPANCY) return '--xl-width';
        if (rateScopeId === RATE_SCOPE_MODELS.PERSON) return '--xl-width';
        return '';
    }, [isFullscreen, rateScopeId]);

    return (
        <>
            {deletePopUpVisible && !readOnlyUser && (
                <DeletePopUp
                    open={!!deletePopUpVisible}
                    onSave={async () => {
                        try {
                            handleLoadingPanel(true);
                            await handleSaveRateDate({
                                ...rateDateEdition,
                                Deleted: true,
                            });
                            setSuccessMessage(
                                FeedbackMessages.COMMON_SUCCESS_DELETE
                            );
                        } catch (error) {
                            setErrorMessage(FeedbackMessages.COMMON_ERROR);
                        } finally {
                            handleLoadingPanel(false);
                            setDeletePopUpVisible(false);
                            onClose();
                        }
                    }}
                    onCancel={() => {
                        setDeletePopUpVisible(false);
                    }}
                ></DeletePopUp>
            )}
            <LateralPanel open={open} className={panelClassname}>
                {isLoadingPanel ? (
                    <LoadingPanel />
                ) : (
                    <form className='flex-column height100perc gap-20'>
                        <div className='flex-between flex-align-center contract-panel__title'>
                            <PanelTitle
                                text={'static_rate'}
                                className='text-secondary-color'
                                translated={true}
                            />
                            <div className='flex gap-10'>
                                <IconButton
                                    iconName='Fullscreen'
                                    onClick={() =>
                                        setIsFullscreen(prev => !prev)
                                    }
                                />
                                <IconButton
                                    iconName='Close'
                                    onClick={onClose}
                                />
                            </div>
                        </div>
                        {readOnlyUser ? (
                            <ReadOnlyUserAlert text='read_only_contract' />
                        ) : (
                            isBlockedContract && (
                                <Alert severity='info'>
                                    {translate('bloqued_information_contract')}
                                </Alert>
                            )
                        )}
                        <div className='flex-column gap-20 flex-grow'>
                            <Grid
                                className=' border-color-block padding-block-20'
                                style={{ width: 'auto', marginBottom: '20px' }}
                                columnSpacing={gridSpacing30}
                                rowSpacing={gridSpacing20}
                            >
                                <GridChild
                                    gridProps={singleLG_DoubleSM_ColumnGrid}
                                >
                                    <Title>{translate('season')}</Title>
                                    <p>{rateDateEdition.season}</p>
                                </GridChild>
                                <GridChild
                                    gridProps={singleLG_DoubleSM_ColumnGrid}
                                >
                                    <Title>{translate('room')}</Title>
                                    <p>{rateDateEdition.room}</p>
                                </GridChild>
                                <GridChild
                                    gridProps={singleLG_DoubleSM_ColumnGrid}
                                >
                                    <Title>{translate('meal_plan')}</Title>
                                    <p>{rateDateEdition.mealPlan}</p>
                                </GridChild>
                                <GridChild
                                    gridProps={singleLG_DoubleSM_ColumnGrid}
                                >
                                    <Title>
                                        {translate('rate_scope_model')}
                                    </Title>
                                    {/* rate scope de la habitación => sino general del contracto */}
                                    <p>
                                        {rateDateEdition.roomScopeModel ||
                                            rateDateEdition.rateScopeModel}
                                    </p>
                                </GridChild>
                                <GridChild
                                    gridProps={singleLG_DoubleSM_ColumnGrid}
                                >
                                    <Title>
                                        {translate('standard_occupancy')}
                                    </Title>
                                    <p>{rateDateEdition.standardOccupancy}</p>
                                </GridChild>
                                <GridChild
                                    gridProps={singleLG_DoubleSM_ColumnGrid}
                                >
                                    <Title>{translate('max_occupancy')}</Title>
                                    <p>{rateDateEdition.maxOccupancy}</p>
                                </GridChild>
                                <GridChild gridProps={doubleMD_ColumnGrid}>
                                    <Title>{translate('occupancy_code')}</Title>
                                    <p className='text-overflow-ellipsis'>
                                        {rateDateEdition.occupancy}
                                    </p>
                                </GridChild>
                            </Grid>

                            <Grid
                                style={{ width: 'auto', marginTop: '20px' }}
                                columnSpacing={gridSpacing30}
                                rowSpacing={gridSpacing30}
                                className='contract-panel__bottom-line'
                            >
                                <GridChild
                                    gridProps={singleLG_DoubleSM_ColumnGrid}
                                >
                                    <TextInput
                                        deleteIcon={false}
                                        required
                                        type='number'
                                        id='initialInventory'
                                        labelText='inventory_initial'
                                        value={rateDateEdition.initialInventory}
                                        disabled={
                                            isLoadingPanel ||
                                            isBlockedContract ||
                                            readOnlyUser
                                        }
                                        handleChange={handleInputNumber}
                                        error={!!errors?.initialInventory}
                                        errorMessage={errors?.initialInventory}
                                    />
                                </GridChild>
                                <GridChild
                                    gridProps={singleLG_DoubleSM_ColumnGrid}
                                >
                                    <TextInput
                                        deleteIcon={false}
                                        required
                                        type='number'
                                        id='currentInventory'
                                        labelText='inventary_current'
                                        value={rateDateEdition.currentInventory}
                                        disabled={
                                            isLoadingPanel ||
                                            isBlockedContract ||
                                            readOnlyUser
                                        }
                                        handleChange={handleInputNumber}
                                        inputProps={{
                                            max: rateDateEdition.initialInventory,
                                        }}
                                        error={!!errors?.currentInventory}
                                        errorMessage={errors?.currentInventory}
                                    />
                                </GridChild>
                                <GridChild
                                    gridProps={singleLG_DoubleSM_ColumnGrid}
                                >
                                    <TextInput
                                        deleteIcon={false}
                                        type='number'
                                        id='minStayAtArrival'
                                        labelText='inventory_min_stay_arrival'
                                        value={rateDateEdition.minStayAtArrival}
                                        disabled={
                                            isLoadingPanel ||
                                            isBlockedContract ||
                                            readOnlyUser
                                        }
                                        handleChange={handleInputNumber}
                                    />
                                </GridChild>
                                <GridChild
                                    gridProps={singleLG_DoubleSM_ColumnGrid}
                                >
                                    <TextInput
                                        deleteIcon={false}
                                        type='number'
                                        id='maxStayAtArrival'
                                        labelText='inventory_max_stay_arrival'
                                        value={rateDateEdition.maxStayAtArrival}
                                        disabled={
                                            isLoadingPanel ||
                                            isBlockedContract ||
                                            readOnlyUser
                                        }
                                        handleChange={handleInputNumber}
                                    />
                                </GridChild>
                                <GridChild
                                    gridProps={singleLG_DoubleSM_ColumnGrid}
                                >
                                    <TextInput
                                        deleteIcon={false}
                                        type='number'
                                        id='minBookingAdvancedOffset'
                                        labelText='inventory_min_booking_offset'
                                        value={
                                            rateDateEdition.minBookingAdvancedOffset
                                        }
                                        disabled={
                                            isLoadingPanel ||
                                            isBlockedContract ||
                                            readOnlyUser
                                        }
                                        handleChange={handleInputNumber}
                                    />
                                </GridChild>
                                <GridChild
                                    gridProps={singleLG_DoubleSM_ColumnGrid}
                                >
                                    <TextInput
                                        deleteIcon={false}
                                        type='number'
                                        id='maxBookingAdvancedOffset'
                                        labelText='inventory_max_booking_offset'
                                        value={
                                            rateDateEdition.maxBookingAdvancedOffset
                                        }
                                        disabled={
                                            isLoadingPanel ||
                                            isBlockedContract ||
                                            readOnlyUser
                                        }
                                        handleChange={handleInputNumber}
                                    />
                                </GridChild>
                            </Grid>
                            <div className='flex-column  gap-10 margin-top-16 border-color-block padding-block-20'>
                                <PanelTitle
                                    text={`${translate(
                                        'rate_plan_by_scope_model'
                                    )}:  ${
                                        rateDateEdition.roomScopeModel ||
                                        rateDateEdition.rateScopeModel
                                    }`}
                                />

                                {rateScopeId === RATE_SCOPE_MODELS.ROOM && (
                                    <RatesByRoom
                                        validationError={errors.baseRateRoom}
                                    />
                                )}
                                {rateScopeId ===
                                    RATE_SCOPE_MODELS.OCCUPANCY && (
                                    <RatesByOccupancy
                                        validationError={
                                            errors.baseRateOccupancy
                                        }
                                    />
                                )}
                                {rateScopeId === RATE_SCOPE_MODELS.PERSON && (
                                    <RatesByPerson
                                        validationError={errors.baseRatePerson}
                                    />
                                )}
                            </div>
                        </div>
                        <div
                            className={`flex-${isNewRate ? 'end' : 'between'}`}
                        >
                            {!isNewRate && (
                                <Button
                                    className='delete-button'
                                    onClick={() => {
                                        setDeletePopUpVisible(true);
                                    }}
                                    disabled={
                                        isLoadingPanel ||
                                        isBlockedContract ||
                                        readOnlyUser
                                    }
                                >
                                    {translate('delete')}
                                </Button>
                            )}

                            <div className='flex-end gap-20'>
                                <Button
                                    variant='outlined'
                                    onClick={onClose}
                                    disabled={isLoadingPanel}
                                >
                                    {translate('cancel')}
                                </Button>
                                <Button
                                    variant='contained'
                                    onClick={async () => {
                                        setValidatedForm(true);
                                        const validated = validateForm();
                                        if (validated) {
                                            await handleSaveRateDate();
                                        }
                                    }}
                                    disabled={
                                        isLoadingPanel ||
                                        isBlockedContract ||
                                        readOnlyUser
                                    }
                                >
                                    {translate('save')}
                                </Button>
                            </div>
                        </div>
                    </form>
                )}
            </LateralPanel>
        </>
    );
};

export default RatesPanel;
